import { useEffect, useLayoutEffect, useRef } from "react";
import { useOptionalSelfService, type StepProgress } from "../../context";

/**
 * Where a flow state sits in the flow's named phases. `hidden` removes the
 * stepper (terminal, handoff and error screens, KO-20); `complete` fills it
 * after the flow's work was committed.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const placement: FlowPhasePlacement = { phase: 2, subStep: { current: 1, total: 2 } };
 * ```
 */
export type FlowPhasePlacement =
  | { readonly kind?: "phase"; readonly phase: number; readonly subStep?: { readonly current: number; readonly total: number } }
  | { readonly kind: "complete" }
  | { readonly kind: "hidden" };

/**
 * Builds the footer progress for a flow from translated phase names and the
 * current state's placement (named phases, sub-steps, completion).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * flowPhaseProgress(["Anmelden", "Termin", "Bestätigen"], { phase: 1 });
 * // { current: 1, total: 3, phases: [...] }
 * ```
 */
export function flowPhaseProgress(
  phases: readonly string[],
  placement: FlowPhasePlacement,
): StepProgress | null {
  if (placement.kind === "hidden") return null;
  if (placement.kind === "complete") {
    return { current: phases.length, total: phases.length, phases, complete: true };
  }
  return {
    current: placement.phase,
    total: phases.length,
    phases,
    ...(placement.subStep === undefined ? {} : { subStep: placement.subStep }),
  };
}

/**
 * Publishes a flow's named-phase progress to the shell footer with the
 * existing `setStepProgress` API (KO-20). No-ops outside the self-service
 * shell. The publication is keyed by content, so a fresh object with the same
 * values does not republish.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * usePublishFlowPhaseProgress(flowPhaseProgress(phases, placementFor(state)));
 * ```
 */
export function usePublishFlowPhaseProgress(progress: StepProgress | null): void {
  const setStepProgress = useOptionalSelfService()?.setStepProgress;
  const key = progress === null ? "" : JSON.stringify(progress);
  const latest = useRef(progress);
  useLayoutEffect(function syncLatestFlowProgress() {
    latest.current = progress;
  });
  useEffect(
    function publishFlowPhaseProgress() {
      setStepProgress?.(latest.current);
    },
    [key, setStepProgress],
  );
}
